import {FormSection} from '@/components/commons/form-section/FormSection'
import {Controller, useFormContext, useWatch} from 'react-hook-form'
import {MedicalReportFieldsSchema} from '@/features/medical-reports/formSchemas'
import {z} from 'zod'
import {useTranslation} from '@/translations/i18n'
import {useOutletContext} from 'react-router-dom'
import {PatientExtended} from '@/features/patients/types'
import {BannerAlert} from '@/components/commons/banner-alert/BannerAlert'
import {useResetMedicalReportForm} from '../../hooks/useResetMedicalReportForm'
import {InputDate} from '@/components/commons/input-date/InputDate'
import {useProjectsByPatientDate} from '@/features/projects/hooks/useProjectsByPatientDate'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Label} from '@/components/ui/label/Label'
import {useTheme} from 'styled-components'
import {useGetInfiniteMedicalReportsByPatient} from '../../queries/useGetInfiniteMedicalReportsByPatient'
import {formatDate} from '@/dayjs'
import {EMPTY_MEDICAL_REPORT_TYPE, isReportTypeProjectCompliant, medicalReportKeyMapToLabel} from '../../utils'
import {useURLParsedParams} from '@/hooks/useURLParsedParams'
import {PatientDetailsMedicalRecordUrlParams} from '../../types'

const MedicalReportDateValidationSchema = MedicalReportFieldsSchema.pick({
    reportDate: true,
    project: true,
    projectId: true,
    reportType: true,
    reportTypeKeyName: true,
    teamMembers: true,
    accesses: true
})
type MedicalReportDateValidationSchema = z.infer<typeof MedicalReportDateValidationSchema>

export const MedicalReportDateSection = () => {
    const {t} = useTranslation()

    const urlParams = useURLParsedParams(PatientDetailsMedicalRecordUrlParams)

    const form = useFormContext<MedicalReportDateValidationSchema>()
    const {patient} = useOutletContext<{patient: PatientExtended}>()

    const reportDate = useWatch({control: form.control, name: 'reportDate'})
    const reportType = useWatch({control: form.control, name: 'reportType'})
    const project = useWatch({control: form.control, name: 'project'})

    const {palette} = useTheme()

    const resetMedicalReportForm = useResetMedicalReportForm({
        fieldsToKeep: [
            'teamMembers',
            'projectId',
            'reportDate',
            'project',
            'medicalRecordId',
            'reportType',
            'reportTypeKeyName',
            'status'
        ],
        reportDate,
        currentReportType: reportType
    })

    const accesses = useWatch({control: form.control, name: 'accesses'})

    const {isErrorElement, hideErrorElement, isLoading} = useProjectsByPatientDate({
        patientId: patient.id,
        date: reportDate,
        currentProject: project,
        setCurrentProject: nextProject => {
            form.setValue('project', nextProject, {shouldDirty: true})
            form.setValue('projectId', nextProject?.id, {shouldDirty: true})

            // Changing the date can move the report onto a different project, or off any project:
            // a report type bound to the previous project must not survive the switch.
            const isTypeStillCompliant = isReportTypeProjectCompliant(reportType?.keyName, nextProject)
            if (!isTypeStillCompliant) {
                form.setValue('reportType', EMPTY_MEDICAL_REPORT_TYPE, {shouldDirty: true})
                form.setValue('reportTypeKeyName', EMPTY_MEDICAL_REPORT_TYPE.keyName, {shouldDirty: true})
            }

            // Clears every type-specific field, keeping the report type value set just above
            resetMedicalReportForm(isTypeStillCompliant ? reportType : EMPTY_MEDICAL_REPORT_TYPE)
        },
        enabled: !accesses
    })

    const reportsByProjectQuery = useGetInfiniteMedicalReportsByPatient({
        urlParams: {patientId: project?.patientId ?? ''},
        params: {
            startDate: formatDate(reportDate),
            endDate: formatDate(reportDate),
            projectId: project?.id,
            medicalRecordReportTypeId: reportType.id,
            limit: 1
        },
        options: {enabled: !!project?.id && !!reportType.id && !!patient.id}
    })

    const hasReportWithSameDate =
        !accesses &&
        urlParams.success &&
        urlParams.data?.medicalReportId &&
        reportsByProjectQuery.remappedData.length > 0
            ? !reportsByProjectQuery.isLoading &&
              !reportsByProjectQuery.remappedData.some(report => report.id === urlParams.data.medicalReportId)
            : false

    return (
        <>
            {isErrorElement && (
                <BannerAlert
                    title={t('errors:default')}
                    description={t('medical_records:error_project_query')}
                    onClose={hideErrorElement}
                />
            )}
            <FormSection title={t('medical_records:report_date')} hasBottomDivider>
                <Flexbox direction="column" gap={1.5}>
                    <Label>{t('medical_records:report_date')}</Label>
                    <Flexbox align="center" gap={2}>
                        <Controller
                            name="reportDate"
                            control={form.control}
                            render={({field, fieldState}) => (
                                <InputDate
                                    placeholder="GG/MM/AAAA"
                                    errorMessage={
                                        hasReportWithSameDate
                                            ? t('medical_records:error_report_date', {
                                                  medicalReportType: t(medicalReportKeyMapToLabel[reportType.keyName])
                                              })
                                            : fieldState.error?.message
                                    }
                                    value={field.value}
                                    onChange={field.onChange}
                                    disabled={!!accesses}
                                />
                            )}
                        />
                        {isLoading && <Spinner size={16} color={palette.neutral['400']} />}
                    </Flexbox>
                </Flexbox>
            </FormSection>
        </>
    )
}
